import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

const PROCESS_IMG = "https://images.unsplash.com/photo-1758518727193-a8da31447858?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzc1NDY0MjcyfDA&ixlib=rb-4.1.0&q=85";

const defaultSteps = [
  {
    num: "01",
    title: "Application",
    desc: "Submit your certification application. GACA reviews your scope, assigns an audit team, and provides a detailed quotation for the certification engagement.",
    details: ["Define certification scope and objectives", "Identify applicable standard(s)", "Audit team assignment", "Quotation and agreement"],
  },
  {
    num: "02",
    title: "Gap Analysis",
    desc: "Optional pre-assessment to identify areas requiring improvement before the formal audit. Our experts conduct a thorough review of your current management system.",
    details: ["Current state assessment", "Document and process review", "Gap identification report", "Remediation roadmap and guidance"],
  },
  {
    num: "03",
    title: "Stage 1 Audit",
    desc: "Documentation review and management system readiness assessment. This determines whether your organization is ready for the Stage 2 certification audit.",
    details: ["Management system documentation review", "Policy and procedure assessment", "Risk assessment evaluation", "Readiness determination for Stage 2"],
  },
  {
    num: "04",
    title: "Stage 2 Audit",
    desc: "On-site certification audit evaluating implementation effectiveness and compliance. Conducted by certified lead auditors with domain expertise.",
    details: ["On-site implementation review", "Staff interviews and evidence gathering", "Control effectiveness testing", "Non-conformity reporting and corrective actions"],
  },
  {
    num: "05",
    title: "Certification & Surveillance",
    desc: "Certificate issuance through our accredited partner bodies, followed by annual surveillance audits to maintain certification status.",
    details: ["Certification issuance via accredited partner", "Certificate registration and publication", "Annual surveillance audit scheduling", "Continuous improvement support"],
  },
];

export default function ProcessPage() {
  const { t } = useLang();
  const pp = t?.process_page || {};
  const tSteps = pp.steps_detail || [];

  const steps = defaultSteps.map((def, i) => {
    const ts = tSteps[i];
    return {
      num: def.num,
      title: ts?.title || def.title,
      desc: ts?.desc || def.desc,
      details: ts?.details || def.details,
    };
  });

  return (
    <div className="page-transition pt-20" data-testid="process-page">
      <section className="bg-[#0B2545] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">
            {pp.badge || "Certification Process"}
          </p>
          <h1 data-testid="process-page-heading" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
            {pp.title || "Your Path to Certification"}
          </h1>
          <p className="text-base md:text-lg text-white/65 max-w-2xl leading-relaxed">
            {pp.subtitle || "Our structured five-step process ensures a smooth path to certification, delivered in partnership with internationally accredited certification bodies."}
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#F5F7FA] border-b border-[#0B2545]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-5 gap-0 relative">
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-[2px] bg-[#13A8BD]/20" />
            {steps.map((step, i) => (
              <div key={step.num} className="relative text-center px-2" data-testid={`process-flow-step-${i}`}>
                <div className="relative z-10 inline-flex items-center justify-center w-16 h-16 bg-white border-2 border-[#13A8BD] text-[#13A8BD] mb-3">
                  <span className="text-xl font-black" style={{ fontFamily: "Chivo, sans-serif" }}>{step.num}</span>
                </div>
                <p className="text-xs font-bold text-[#0B2545] uppercase tracking-wider">{step.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-0 border border-[#0B2545]/10">
            {steps.map((step, i) => (
              <div key={step.num} data-testid={`process-detail-${i}`} className={`grid grid-cols-1 lg:grid-cols-12 gap-0 border-b border-[#0B2545]/10 last:border-b-0 ${i % 2 === 1 ? "bg-[#F5F7FA]" : "bg-white"}`}>
                <div className="lg:col-span-2 p-8 lg:p-12 flex items-start justify-center border-r border-[#0B2545]/10">
                  <span className="text-5xl lg:text-6xl font-black text-[#13A8BD]/20" style={{ fontFamily: "Chivo, sans-serif" }}>{step.num}</span>
                </div>
                <div className="lg:col-span-5 p-8 lg:p-12">
                  <h2 className="text-2xl font-bold text-[#0B2545] mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>{step.title}</h2>
                  <p className="text-base text-slate-600 leading-relaxed">{step.desc}</p>
                </div>
                <div className="lg:col-span-5 p-8 lg:p-12 border-l border-[#0B2545]/10">
                  <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-6">{pp.activities_label || "Key Activities"}</h4>
                  <ul className="space-y-3">
                    {step.details.map((detail) => (
                      <li key={detail} className="flex items-start gap-3 text-sm text-slate-700">
                        <CheckCircle size={16} className="text-[#13A8BD] mt-0.5 flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#F5F7FA] border-t border-[#0B2545]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img src={PROCESS_IMG} alt="Certification consultation" className="w-full h-80 object-cover" data-testid="process-image" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545] mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
                {pp.cta_title || "Start Your Certification Journey"}
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {pp.cta_desc || "Our experienced team will guide you through every step of the certification process. Typical time to certification readiness is 90 days."}
              </p>
              <Link to="/contact" data-testid="process-cta" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
                {pp.cta_button || "Request Certification"} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
